import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faCheckDouble,
  faEnvelope,
  faKey,
  faList,
  faPause,
  faPhoneVolume,
  faRedo,
  faShield,
  faTabletAlt,
  faTag,
  faTasks,
  faTimes,
} from "@fortawesome/pro-solid-svg-icons";

import { faCarMirrors } from "../../util/customIcons";

import {
  APPOINTMENT_LOG_TYPES,
  CHECK_EVENT_TYPE,
  COMMUNICATION_EVENTS,
  CUSTOMER_COMMUNICATION_STATUSES,
  DESK_COMMUNICATION_EVENTS,
  KEYLOCKER_COMMUNICATION_EVENT_TYPE,
  KEYLOCKER_PIN_REQUEST_TYPES,
  KIOSK_COMMUNICATION_EVENT_TYPES,
} from "../../../../../../util/communication_statuses";
import { getStatusIconConfig, getStatusName } from "../../util/statusIcons";
import { ACTIVITY_LOG_TYPE } from "../../util";
import { APPOINTMENT_STATUSES } from "../../../../../../util/common";

import "./index.scss";

const CustomerCommunicationIconAndTitle = ({ eventType, t }) => {
  let title = "";

  switch (eventType) {
    case COMMUNICATION_EVENTS.ONLINE_CHECKIN_SENT:
      title = t("cc_sent_online_checkin").message || "Sent online check-in";
      break;
    case COMMUNICATION_EVENTS.ONLINE_CHECKIN_OPENED:
      title = t("cc_opened_online_checkin").message || "Opened online check-in";
      break;
    case COMMUNICATION_EVENTS.ONLINE_CHECKIN_ANSWERED:
      title = t("cc_answered_online_checkin").message || "Answered online check-in";
      break;
    case COMMUNICATION_EVENTS.DIAGNOSE_OVERVIEW_SENT:
      title = t("cc_sent_diagnose_overview").message || "Sent diagnose overview";
      break;
    case COMMUNICATION_EVENTS.DIAGNOSE_OVERVIEW_OPENED:
      title = t("cc_opened_diagnose_overview").message || "Opened diagnose overview";
      break;
    case COMMUNICATION_EVENTS.DIAGNOSE_OVERVIEW_ANSWERED:
      title = t("cc_answered_diagnose_overview").message || "Answered diagnose overview";
      break;
    case COMMUNICATION_EVENTS.DIAGNOSE_OVERVIEW_RESENT:
      title = t("cc_resent_diagnose_overview").message || "Resent diagnose overview";
      break;
    case COMMUNICATION_EVENTS.REPAIR_OVERVIEW_SENT:
      title = t("cc_sent_repair_overview").message || "Sent repair overview";
      break;
    case COMMUNICATION_EVENTS.REPAIR_OVERVIEW_RESENT:
      title = t("cc_resent_repair_overview").message || "Resent repair overview";
      break;
    case COMMUNICATION_EVENTS.REPAIR_OVERVIEW_OPENED:
      title = t("cc_repair_overview_opened").message || "Repair overview opened";
      break;
    default:
      return null;
  }

  return (
    <span className="activity-type">
      <FontAwesomeIcon icon={faEnvelope} className="activity-type-icon" />
      {title}
    </span>
  );
};

const CheckEventIconAndTitle = ({ eventType, t }) => {
  switch (eventType) {
    case CHECK_EVENT_TYPE.STARTED:
      return (
        <span className="activity-type">
          <FontAwesomeIcon icon={faList} className="activity-type-icon" />
          {t("cc_checklist_started").message || "Checklist started"}
        </span>
      );
    case CHECK_EVENT_TYPE.RESUMED:
      return (
        <span className="activity-type">
          <FontAwesomeIcon icon={faRedo} className="activity-type-icon" />
          {t("cc_checklist_resumed").message || "Checklist resumed"}
        </span>
      );
    case CHECK_EVENT_TYPE.PAUSED:
      return (
        <span className="activity-type">
          <FontAwesomeIcon icon={faPause} className="activity-type-icon" />
          {t("cc_checklist_paused").message || "Checklist paused"}
        </span>
      );
    case CHECK_EVENT_TYPE.ABORTED:
      return (
        <span className="activity-type">
          <FontAwesomeIcon icon={faTimes} className="activity-type-icon" />
          {t("cc_checklist_aborted").message || "Checklist aborted"}
        </span>
      );
    case CHECK_EVENT_TYPE.COMPLETED:
      return (
        <span className="activity-type">
          <FontAwesomeIcon icon={faTasks} className="activity-type-icon" />
          {t("cc_checklist_completed").message || "Checklist completed"}
        </span>
      );
    default:
      return null;
  }
};

const DeskCheckinCommunicationIconAndTitle = ({ eventType, t }) => {
  let title = "";

  switch (eventType) {
    case DESK_COMMUNICATION_EVENTS.CHECKIN_SENT:
      title = t("cc_desk_checkin_sent").message || "Desk check-in sent";
      break;
    case DESK_COMMUNICATION_EVENTS.CHECKIN_OPENED:
      title = t("cc_desk_checkin_opened").message || "Desk check-in opened";
      break;
    case DESK_COMMUNICATION_EVENTS.CHECKIN_FAILED_TO_OPEN:
      title = t("cc_desk_checkin_failed_to_open").message || "Desk check-in failed to open";
      break;
    case DESK_COMMUNICATION_EVENTS.CHECKIN_CANCELED:
      title = t("cc_desk_checkin_canceled").message || "Desk check-in canceled";
      break;
    case DESK_COMMUNICATION_EVENTS.CHECKIN_RECEIVED:
      title = t("cc_desk_checkin_received").message || "Desk check-in received";
      break;
    case DESK_COMMUNICATION_EVENTS.CHECKIN_ANSWERED:
      title = t("cc_desk_checkin_answered").message || "Desk check-in answered";
      break;
    default:
      return null;
  }

  return (
    <span className="activity-type">
      <FontAwesomeIcon icon={faTabletAlt} className="activity-type-icon" />
      {title}
    </span>
  );
};

const DeskCheckoutCommunicationIconAndTitle = ({ eventType, t }) => {
  let title = "";

  switch (eventType) {
    case DESK_COMMUNICATION_EVENTS.CHECKIN_SENT:
      title = t("cc_desk_checkout_sent").message || "Desk check-out sent";
      break;
    case DESK_COMMUNICATION_EVENTS.CHECKIN_OPENED:
      title = t("cc_desk_checkout_opened").message || "Desk check-out opened";
      break;
    case DESK_COMMUNICATION_EVENTS.CHECKIN_FAILED_TO_OPEN:
      title = t("cc_desk_checkout_failed_to_open").message || "Desk check-out failed to open";
      break;
    case DESK_COMMUNICATION_EVENTS.CHECKIN_CANCELED:
      title = t("cc_desk_checkout_canceled").message || "Desk check-out canceled";
      break;
    case DESK_COMMUNICATION_EVENTS.CHECKIN_RECEIVED:
      title = t("cc_desk_checkout_received").message || "Desk check-out received";
      break;
    case DESK_COMMUNICATION_EVENTS.CHECKIN_ANSWERED:
      title = t("cc_desk_checkout_answered").message || "Desk check-out answered";
      break;
    default:
      return null;
  }

  return (
    <span className="activity-type">
      <FontAwesomeIcon icon={faTabletAlt} className="activity-type-icon" />
      {title}
    </span>
  );
};

const KeylockerCommunicationIconAndTitle = ({ eventType, keylockerPinRequestType, activityTitle = "", t }) => {
  let title = "";

  switch (eventType) {
    case KEYLOCKER_COMMUNICATION_EVENT_TYPE.CHECK_IN_OPENED:
      title = t("cc_keylocker_checkin_opened").message || "Keylocker check-in opened";
      break;
    case KEYLOCKER_COMMUNICATION_EVENT_TYPE.CHECK_IN_ANSWERED:
      title = t("cc_keylocker_checkin_answered").message || "Keylocker check-in answered";
      break;
    case KEYLOCKER_COMMUNICATION_EVENT_TYPE.DROP_PIN_SENT:
      title =
        keylockerPinRequestType !== KEYLOCKER_PIN_REQUEST_TYPES.TWO_FA_DISABLED
          ? t("cc_keylocker_drop_pin_sent").message || "Keylocker drop pin sent"
          : t("cc_2fa_disabled_no_pin_sent").message || "2FA disabled, no PIN sent";
      break;
    case KEYLOCKER_COMMUNICATION_EVENT_TYPE.PICK_UP_PIN_SENT:
      title = t("cc_keylocker_pick_up_pin_sent").message || "Keylocker pick up pin sent";
      break;
    default:
      title = activityTitle;
  }

  return (
    <span className="activity-type">
      <FontAwesomeIcon icon={faKey} className="activity-type-icon" />
      {title}
    </span>
  );
};

const KioskCommunicationIconAndTitle = ({ eventType, t }) => {
  let title = "";

  switch (eventType) {
    case KIOSK_COMMUNICATION_EVENT_TYPES.CHECK_IN_OPENED:
      title = t("cc_kiosk_checkin_opened").message || "Kiosk check-in opened";
      break;
    case KIOSK_COMMUNICATION_EVENT_TYPES.CHECK_IN_ANSWERED:
      title = t("cc_kiosk_checkin_answered").message || "Kiosk check-in answered";
      break;
    case KIOSK_COMMUNICATION_EVENT_TYPES.LABEL_DETACHED:
      title = t("cc_label_detached").message || "Label detached";
      break;
    default:
      return null;
  }

  return (
    <span className="activity-type">
      <FontAwesomeIcon icon={faTag} className="activity-type-icon" />
      {title}
    </span>
  );
};

const renderStatusIcon = (statusIdentifier, appointment) => {
  if (statusIdentifier === APPOINTMENT_STATUSES.BACK_ORDER) {
    return <span className="activity-type-text-badge">BO</span>;
  }

  if (statusIdentifier === APPOINTMENT_STATUSES.CONTACT) {
    const isMail = Number(appointment?.customcom_status) >= CUSTOMER_COMMUNICATION_STATUSES.DIAGNOSE_OVERVIEW_SENT;
    return <FontAwesomeIcon icon={isMail ? faEnvelope : faPhoneVolume} className="activity-type-icon" />;
  }

  if (statusIdentifier === APPOINTMENT_STATUSES.CAR_OK_PLUS_REPAIR_OVERVIEW) {
    return (
      <span className="activity-type-composite-icon">
        <FontAwesomeIcon icon={faCheckDouble} className="activity-type-icon" />
        <FontAwesomeIcon icon={faEnvelope} className="activity-type-composite-icon-overlay" />
      </span>
    );
  }

  if (statusIdentifier === APPOINTMENT_STATUSES.QUALITY_CHECK_PLUS_REPAIR_OVERVIEW) {
    return (
      <span className="activity-type-composite-icon">
        <FontAwesomeIcon icon={faShield} className="activity-type-icon" />
        <FontAwesomeIcon icon={faEnvelope} className="activity-type-composite-icon-overlay" />
      </span>
    );
  }

  if (statusIdentifier === APPOINTMENT_STATUSES.CHECK_IN_DONE) {
    return (
      <span className="activity-type-composite-icon">
        <FontAwesomeIcon icon={faCarMirrors} className="activity-type-icon" />
        <FontAwesomeIcon icon={faCalendarCheck} className="activity-type-composite-icon-overlay" />
      </span>
    );
  }

  const config = getStatusIconConfig(statusIdentifier);
  return <FontAwesomeIcon icon={config.icon} style={{ color: config.color }} className="activity-type-icon" />;
};

const AppointmentStatusIconAndTitle = ({ statusIdentifier, appointmentLogType, appointment, t }) => {
  if (appointmentLogType === APPOINTMENT_LOG_TYPES.CUSTOMER_OWNER_CHANGED) {
    return (
      <span className="activity-type activity-type--customer-owner">
        <span className="activity-type-co-badge">CO</span>
        {t("cc_new_customer_owner").message || "New customer owner"}
      </span>
    );
  }

  const name = getStatusName(statusIdentifier, appointment?.dealer_location?.statuses, t);

  return (
    <span className="activity-type">
      {renderStatusIcon(statusIdentifier, appointment)}
      {name}
    </span>
  );
};

const ActivityType = ({ activityType, eventType, statusIdentifier, isDeskCheckOut, keylockerPinRequestType, activityTitle, appointmentLogType, appointment, t }) => {
  switch (activityType) {
    case ACTIVITY_LOG_TYPE.APPOINTMENT:
      return <AppointmentStatusIconAndTitle statusIdentifier={statusIdentifier} appointmentLogType={appointmentLogType} appointment={appointment} t={t} />;

    case ACTIVITY_LOG_TYPE.CHECK_EVENT:
      return <CheckEventIconAndTitle eventType={eventType} t={t} />;

    case ACTIVITY_LOG_TYPE.CUSTOMER:
      return <CustomerCommunicationIconAndTitle eventType={eventType} t={t} />;

    case ACTIVITY_LOG_TYPE.DESK:
      if (isDeskCheckOut) return <DeskCheckoutCommunicationIconAndTitle eventType={eventType} t={t} />;
      return <DeskCheckinCommunicationIconAndTitle eventType={eventType} t={t} />;

    case ACTIVITY_LOG_TYPE.KEYLOCKER:
      return <KeylockerCommunicationIconAndTitle eventType={eventType} keylockerPinRequestType={keylockerPinRequestType} activityTitle={activityTitle} t={t} />;

    case ACTIVITY_LOG_TYPE.KIOSK:
      return <KioskCommunicationIconAndTitle eventType={eventType} t={t} />;

    default:
      return null;
  }
};

export default ActivityType;
